import {
  TopicRequestBuilder,
  getTopicOrTopicGroupType,
  useBaseTopicRequestBuilder
} from 'src/components/BeaconV2Components/ExperimentalLayout/Traffic/utils';
import { ComparisonPeriod, useAppSelector, useComparisonPeriod } from 'src/utils/Hooks';
import { useQuery } from '@tanstack/react-query';
import { TopicCardRecord } from '../../../Topics/types';
import { calculatePercentChange } from 'src/utils/app';
import { getNextYearWeekId, weekIdToTimestamp } from 'src/utils/dateUtils';
import { useMainEntityId } from 'src/utils/Hooks/reduxSelectors';
import { GetTopicGroupSummaryPageMetricsResponseRecord, topicSettingsServerProxy } from '../serverProxy';
import _isNil from 'lodash/isNil';
import { userMetadataServerProxy } from 'src/serverProxy/userMetadataServerProxy';
import { TOPIC_QUERY_KEYS } from 'src/components/BeaconV2Components/ExperimentalLayout/Traffic/Settings/topicQueryKeys';
import { useComparisonWeeksForSpline } from 'src/components/BeaconV2Components/ExperimentalLayout/Topics/hooks/useTopics';

/**
 * Fetches the list of topic groups for the topics settings page
 */
export function useTopicGroupInclusions() {
  const { startWeek, endWeek } = useAppSelector((state) => state.mainTimePeriod);

  const comparisonPeriod = useComparisonPeriod();
  const { comparisonStartWeek, comparisonEndWeek } = useComparisonWeeksForSpline();

  const baseTopicGroupsMetrics = useBaseTopicRequestBuilder();

  const retailerId = useAppSelector((state) => state.retailer.id);
  const topicId = useMainEntityId();

  const topicGroupMetricsRequest = new TopicRequestBuilder()
    .setStartWeekId(startWeek)
    .setEndWeekId(endWeek)
    .setSortField('totalTraffic')
    .setTopicType('All');

  const topicGroupMetricsComparisonRequest = new TopicRequestBuilder(topicGroupMetricsRequest.build())
    .setStartWeekId(comparisonStartWeek)
    .setEndWeekId(comparisonEndWeek);

  const { data: topicGroups, isLoading } = useQuery({
    queryKey: [
      TOPIC_QUERY_KEYS.all,
      retailerId,
      topicId,
      topicGroupMetricsRequest.build(),
      topicGroupMetricsComparisonRequest.build()
    ],
    queryFn: async () => {
      const response = await topicSettingsServerProxy.getTopicGroupsContainingTopics({
        retailerId: +retailerId,
        topicIds: [topicId.toString()]
      });

      const topicGroupIds = response.records.map((record) => record.topicGroupIds).flat();

      async function getTopicGroupMetadata() {
        if (topicGroupIds.length === 0) {
          return {};
        }

        const topicGroupsMetadata = await topicSettingsServerProxy.bulkGetTopicGroups(topicGroupIds);

        return topicGroupsMetadata;
      }

      const [
        topicGroupsByWeek,
        topicGroupsByComparisonWeek,
        topicGroupMetadata,
        topicGroupMetrics,
        topicGroupComparisonMetrics
      ] = await Promise.all([
        topicGroupIds.length > 0
          ? topicSettingsServerProxy.getTopicGroupMetricsByWeek(
              new TopicRequestBuilder(baseTopicGroupsMetrics.build())
                .setStartWeekId(startWeek)
                .setEndWeekId(endWeek)
                .setTopicGroupIds(topicGroupIds)
                .setTopicIds([])
                .build()
            )
          : Promise.resolve({ records: [] }),
        topicGroupIds.length > 0
          ? topicSettingsServerProxy.getTopicGroupMetricsByWeek(
              new TopicRequestBuilder(baseTopicGroupsMetrics.build())
                .setStartWeekId(comparisonStartWeek)
                .setEndWeekId(comparisonEndWeek)
                .setTopicGroupIds(topicGroupIds)
                .setTopicIds([])
                .build()
            )
          : Promise.resolve({ records: [] }),
        getTopicGroupMetadata(),
        topicSettingsServerProxy.getTopicGroupSummaryPageMetrics(
          topicGroupMetricsRequest.setTopicGroupIds(topicGroupIds).setResultCount(topicGroupIds.length).build()
        ),
        topicSettingsServerProxy.getTopicGroupSummaryPageMetrics(
          topicGroupMetricsComparisonRequest
            .setTopicGroupIds(topicGroupIds)
            .setResultCount(topicGroupIds.length)
            .build()
        )
      ]);

      async function getUserMetadata() {
        const userIds = [
          ...new Set([...Object.values(topicGroupMetadata)].map((topicGroup) => topicGroup?.ownedByUserId))
        ].filter((item) => !_isNil(item));

        if (userIds.length < 1) {
          return {};
        }

        const userMetadata = await userMetadataServerProxy.getUserProfiles(userIds);
        return userMetadata;
      }
      // Get User Metadata
      const userMetadata = await getUserMetadata();

      const topicGroupIdMap = topicGroupMetrics.records.reduce((acc, record) => {
        acc.set(record.topicGroupId, record);
        return acc;
      }, new Map<string, GetTopicGroupSummaryPageMetricsResponseRecord>());

      const comparisonTopicGroupIdMap = topicGroupComparisonMetrics.records.reduce((acc, record) => {
        acc.set(record.topicGroupId, record);
        return acc;
      }, new Map<string, GetTopicGroupSummaryPageMetricsResponseRecord>());

      const topicCardRecords: Omit<TopicCardRecord, 'topicGroups'>[] = topicGroupIds
        .map((topicGroupId) => {
          const { ownedByUserId, sharedWithEntityId, topicGroupName } = topicGroupMetadata?.[topicGroupId] ?? {};

          const totalTraffic = topicGroupIdMap.get(topicGroupId)?.totalTraffic ?? 0;
          const totalComparisonTraffic = comparisonTopicGroupIdMap.get(topicGroupId)?.totalTraffic ?? 0;

          const splineData = topicGroupsByWeek?.records
            .filter((weekRecord) => weekRecord.topicGroupId === topicGroupId)
            .sort((a, b) => a.weekId - b.weekId)
            .map((weekRecord) => [weekIdToTimestamp(weekRecord.weekId), weekRecord.totalTraffic]);

          const comparisonSplineData = topicGroupsByComparisonWeek.records
            .filter((weekRecord) => weekRecord.topicGroupId === topicGroupId)
            .map((weekRecord) => {
              let { weekId } = weekRecord;
              // If we're comparing YTD against prior year, only compare against the same weeks
              if (comparisonPeriod === ComparisonPeriod.PRIOR_YEAR) {
                weekId = weekIdToTimestamp(getNextYearWeekId(weekRecord.weekId));
              } else {
                weekId = weekIdToTimestamp(weekRecord.weekId);
              }
              return [weekId, weekRecord.totalTraffic];
            })
            .sort((a, b) => a[0] - b[0]);

          // Add the last data point from the comparison data to the primary spline data to bridge the gap during prior period comparison
          if (comparisonPeriod === ComparisonPeriod.PRIOR_PERIOD && comparisonSplineData.length > 0) {
            splineData.unshift(comparisonSplineData[comparisonSplineData?.length - 1]);
          }

          return {
            id: topicGroupId,
            owner: userMetadata[ownedByUserId],
            topicMetadata: topicGroupMetadata?.[topicGroupId],
            name: topicGroupName,
            type: 'topicGroup' as const,
            totalTraffic,
            totalTrafficPercentChange: calculatePercentChange(totalTraffic, totalComparisonTraffic),
            splineData,
            comparisonSplineData,
            ownership: getTopicOrTopicGroupType(sharedWithEntityId, ownedByUserId)
          };
        })
        .sort((a, b) => b.totalTraffic - a.totalTraffic);

      return topicCardRecords;
    }
  });

  return {
    topicGroups: topicGroups ?? [],
    isLoading
  };
}
